/**
 * 路由导航后回到页面顶部。
 *
 * 背景：SPA 内部导航（如广场下载引导跳 /pricing）默认保留原滚动偏移，
 * 在长列表页滚到很深的位置后跳转，新页面会停在相同（或被钳制）的滚动位置，
 * 顶部内容看不到（定价页的套餐区正好在页首）。
 *
 * 规则：
 * - POP（浏览器后退/前进）：不动，保留用户原浏览位置
 * - 仅语言前缀变化（LocaleSwitcher 同页切 /:lang）：不动
 * - 路由态弹窗导航（state.background，如「我的订阅」）：不动，背景页原地保留
 * - 带 hash 锚点（如博客顶栏 Link 到 /:lang#how）：滚到对应元素
 * - 其余 PUSH/REPLACE 导航：滚动到顶部
 */

import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";
import { stripLang } from "../lib/lang";

export default function ScrollToTop() {
  const { pathname, hash, state } = useLocation();
  const navType = useNavigationType();
  const prevPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    const prev = prevPathnameRef.current;
    prevPathnameRef.current = pathname;
    // 首次挂载（直接打开/刷新某 URL）：不强制滚动
    if (prev === null) return;
    // 浏览器后退/前进：保留位置
    if (navType === "POP") return;
    // 路由态弹窗（如「我的订阅」，见 App.tsx）：背景页保持原滚动位置
    if ((state as { background?: unknown } | null)?.background) return;
    // 仅语言前缀变化（同页切语言）：保留位置。
    // 必须排在 hash 判断之前——切语言时 LocaleSwitcher 原样保留 search/hash，
    // URL 里的 #pricing 只是从旧 URL 带过来的残留，不代表锚点导航意图；
    // 否则在 /zh#pricing 上切英文会被误判为锚点导航，滚回 pricing 区块。
    if (prev !== pathname && stripLang(prev) === stripLang(pathname)) return;
    // hash 锚点：优先滚到目标元素（跨页锚点 Link，如博客顶栏 → /:lang#how；
    // behavior 用 auto 以遵循 CSS scroll-behavior 的平滑设置）
    if (hash) {
      document.getElementById(hash.slice(1))?.scrollIntoView();
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname, hash, state, navType]);

  return null;
}
